// ─── Timeline maths ──────────────────────────────────────────────────────────
//
// The Timeline's logic, kept as pure functions away from the components so it
// can be tested without a DOM — and so the fill rule, which the library states
// twice and calls "the actual rule", lives in exactly one place.

/** A scheduled window. Real date-times throughout, never "time within a day" —
 *  a 23:00–02:00 shift is one block that crosses midnight. */
export type Span = { startsAt: Date; endsAt: Date };

export const MINUTE = 60_000;
export const DAY = 24 * 60 * MINUTE;

/** V1 snapping increment for dragging, in minutes. */
export const SNAP_MINUTES = 15;

/**
 * How full a View-state timeline bar is.
 *
 * "Fill logic, confirmed 2026-08-05 (this is the actual rule, not just the
 * visual idea): time-driven, against the activity's scheduled start and end.
 * Before the scheduled start, empty. During the window, fills proportionally
 * to elapsed time. At the scheduled end, complete. No other factor (e.g.
 * actual on-the-ground progress reported by a person) feeds this for V1."
 *
 * Worth being blunt about the consequence, because it is easy to misread the
 * bar: this is a clock against a schedule, NOT a progress report. A full bar
 * means the scheduled window has elapsed, not that anything was finished. The
 * one thing that must never happen is the UI implying confirmed progress from
 * a number that is only arithmetic — that would be the Reliability principle
 * broken by a progress bar.
 */
export function fillFraction(span: Span, now: Date = new Date()): number {
  const start = span.startsAt.getTime();
  const end = span.endsAt.getTime();
  if (end <= start) return 0;
  const t = now.getTime();
  if (t <= start) return 0;
  if (t >= end) return 1;
  return (t - start) / (end - start);
}

/** Round a time to the nearest snap increment. Used while dragging. */
export function snapTime(date: Date, snapMinutes: number = SNAP_MINUTES): Date {
  const ms = snapMinutes * MINUTE;
  return new Date(Math.round(date.getTime() / ms) * ms);
}

/** Where a moment sits along a window, 0–1, clamped to the visible range. */
export function positionOf(window: Span, at: Date): number {
  const start = window.startsAt.getTime();
  const end = window.endsAt.getTime();
  if (end <= start) return 0;
  return Math.min(1, Math.max(0, (at.getTime() - start) / (end - start)));
}

/** The inverse — what moment a fractional position along the window means.
 *  Snapped, because this is what a drag resolves to. */
export function timeAt(window: Span, fraction: number, snapMinutes = SNAP_MINUTES): Date {
  const start = window.startsAt.getTime();
  const end = window.endsAt.getTime();
  const clamped = Math.min(1, Math.max(0, fraction));
  return snapTime(new Date(start + clamped * (end - start)), snapMinutes);
}

/** Left/width percentages for rendering a block inside a window. */
export function blockGeometry(window: Span, block: Span): { left: number; width: number } {
  const left = positionOf(window, block.startsAt);
  const right = positionOf(window, block.endsAt);
  return { left: left * 100, width: Math.max(0, right - left) * 100 };
}

export function overlaps(a: Span, b: Span): boolean {
  // Touching blocks do not overlap — back-to-back shifts are normal, and
  // flagging them would train people to dismiss the warning.
  return a.startsAt < b.endsAt && a.endsAt > b.startsAt;
}

export function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

export function addDays(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Split a span into one piece per calendar day it touches.
 *
 * The data model keeps a cross-midnight block whole; the UI still has to draw
 * it on two day-pages. "Maintain clean calendar-day boundaries internally even
 * where an activity crosses midnight (the UI can show visual continuity across
 * the boundary)" — so each piece is flagged with whether it continues.
 */
export function splitAcrossDays(span: Span): (Span & { continuesBefore: boolean; continuesAfter: boolean })[] {
  const pieces: (Span & { continuesBefore: boolean; continuesAfter: boolean })[] = [];
  let cursor = span.startsAt;
  while (cursor < span.endsAt) {
    const dayEnd = addDays(startOfDay(cursor), 1);
    const pieceEnd = dayEnd < span.endsAt ? dayEnd : span.endsAt;
    pieces.push({
      startsAt: cursor,
      endsAt: pieceEnd,
      continuesBefore: cursor > span.startsAt,
      continuesAfter: pieceEnd < span.endsAt,
    });
    cursor = pieceEnd;
  }
  return pieces;
}

export type DaySlot =
  | { kind: "day"; date: Date; label: string; dayOfMonth: number }
  | { kind: "gap"; count: number };

/**
 * The day selector's contents for an operational range.
 *
 * "Short ranges can show something like `FRI 28 · SAT 29 · SUN 30` with the
 * active day highlighted; longer ranges need the day selector itself to
 * intelligently scroll/condense rather than trying to render dozens or
 * hundreds of dates at once — **don't fall back to anonymous dots** once the
 * range gets substantial, date information has to stay legible."
 *
 * So condensing keeps real dates around the active day and the two ends, and
 * replaces the runs between them with an explicit "+N" that says how many days
 * are hidden. A reader can always tell what they are looking at and how much
 * they are not. The exact mechanism for very long ranges is flagged in the
 * library as undesigned — this is a legible default, not a claim to have
 * solved that.
 */
export function buildDaySlots(
  rangeStart: Date, rangeEnd: Date, active: Date, maxSlots = 7
): DaySlot[] {
  const first = startOfDay(rangeStart);
  const last = startOfDay(rangeEnd);
  const total = Math.max(0, Math.round((last.getTime() - first.getTime()) / DAY)) + 1;

  const day = (i: number): DaySlot => {
    const date = addDays(first, i);
    return {
      kind: "day",
      date,
      label: date.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase(),
      dayOfMonth: date.getDate(),
    };
  };

  if (total <= maxSlots) return Array.from({ length: total }, (_, i) => day(i));

  const activeIndex = Math.min(
    total - 1,
    Math.max(0, Math.round((startOfDay(active).getTime() - first.getTime()) / DAY))
  );

  // Always keep: the first day, the last day, and a window around the active
  // day. Everything else collapses into counted gaps.
  const keep = new Set<number>([0, total - 1]);
  const around = Math.max(1, Math.floor((maxSlots - 2) / 2));
  for (let i = activeIndex - around; i <= activeIndex + around; i++) {
    if (i >= 0 && i < total) keep.add(i);
  }

  const sorted = [...keep].sort((a, b) => a - b);
  const slots: DaySlot[] = [];
  let previous: number | null = null;
  for (const i of sorted) {
    if (previous !== null && i - previous > 1) {
      slots.push({ kind: "gap", count: i - previous - 1 });
    }
    slots.push(day(i));
    previous = i;
  }
  return slots;
}

/**
 * Consecutive calendar days for the compact dot indicator.
 *
 * "A small row of dots (~7 positions by default) represents **consecutive
 * calendar days**, not just days with activity — gaps stay visible as
 * inactive/grey dots rather than being skipped." Skipping empty days would
 * make a sparse schedule look busy, which is a small lie of exactly the kind
 * this product is meant not to tell.
 */
export function buildDayDots(
  from: Date, days: number, spans: Span[]
): { date: Date; active: boolean }[] {
  const start = startOfDay(from);
  return Array.from({ length: days }, (_, i) => {
    const date = addDays(start, i);
    const dayEnd = addDays(date, 1);
    return {
      date,
      active: spans.some(s => s.startsAt < dayEnd && s.endsAt > date),
    };
  });
}

/** HH:MM in the viewer's locale, which is all a timeline label ever needs. */
export function timeLabel(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}
